import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { kanal, type Benutzer } from '@beholder/shared';
import type {
  KnotenFelder,
  KonfliktAntwort,
  MindmapAnwesend,
  MindmapKarte,
  MindmapKnoten,
  MindmapLiveVomServer,
  MindmapSpalte,
  MindmapStand,
  SpaltenOption,
  SpaltenTyp,
} from '@beholder/shared/module/mindmap';
import { api, ApiFehler, modulPfad } from '../../core/api';
import { live, useLive } from '../../core/live';
import { baueBaum, nachkommen, zwischenwert } from './baum';

/** UUID v4 – auch ohne sichere Verbindung (crypto.randomUUID gibt es nur über HTTPS/localhost). */
export function neueId(): string {
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

interface Schritt {
  text: string;
  /** true = hat geklappt (sonst fällt der Schritt weg). */
  rueck(): Promise<boolean>;
  vor(): Promise<boolean>;
}

interface Aktionen {
  anlegen(elternId: string, index: number, titel: string, extra?: KnotenFelder, id?: string, merk?: boolean): Promise<string>;
  aendern(id: string, felder: KnotenFelder, basis?: KnotenFelder, erzwingen?: boolean, merk?: boolean): Promise<boolean>;
  verschieben(id: string, elternId: string, index: number, merk?: boolean): Promise<boolean>;
  /** In den Papierkorb legen; true = geklappt. */
  loeschen(id: string, merk?: boolean): Promise<boolean>;
  /** Aus dem Papierkorb zurückholen (für Rückgängig). */
  wiederherstellen(id: string): Promise<boolean>;
}

/** Werte genau der Felder, die in `felder` vorkommen. */
function felderVon(k: MindmapKnoten, felder: KnotenFelder): KnotenFelder {
  const aus: KnotenFelder = {};
  if (felder.titel !== undefined) aus.titel = k.titel;
  if (felder.beschreibung !== undefined) aus.beschreibung = k.beschreibung;
  if (felder.farbe !== undefined) aus.farbe = k.farbe;
  if (felder.symbol !== undefined) aus.symbol = k.symbol;
  if (felder.werte) aus.werte = Object.fromEntries(Object.keys(felder.werte).map((sid) => [sid, k.werte[sid] ?? null]));
  return aus;
}

export interface Konflikt {
  knotenId: string;
  server: MindmapKnoten;
  meine: KnotenFelder;
  felder: string[];
}

interface Zustand {
  karte: MindmapKarte;
  spalten: MindmapSpalte[];
  knoten: Record<string, MindmapKnoten>;
}

function alsZustand(s: MindmapStand): Zustand {
  return { karte: s.karte, spalten: s.spalten, knoten: Object.fromEntries(s.knoten.map((k) => [k.id, k])) };
}

/** PATCH mit Zugriff auf die 409-Antwort (der Kern-Client gibt nur den Text weiter). */
async function patchKnoten(pfad: string, body: unknown): Promise<{ ok: true; knoten: MindmapKnoten } | { ok: false; konflikt: KonfliktAntwort }> {
  const r = await fetch(pfad, {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: { 'x-beholder': '1', 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  const daten = (await r.json().catch(() => null)) as unknown;
  if (r.status === 409 && (daten as KonfliktAntwort | null)?.knoten) return { ok: false, konflikt: daten as KonfliktAntwort };
  if (!r.ok) throw new ApiFehler(r.status, (daten as { fehler?: string } | null)?.fehler ?? `Fehler ${r.status}`);
  return { ok: true, knoten: daten as MindmapKnoten };
}

/**
 * Zustand einer Karte: lädt, hört live mit, ändert (sofort sichtbar, dann vom Server bestätigt)
 * und meldet Konflikte, wenn jemand anderes dasselbe Feld inzwischen geändert hat.
 */
export function useKarte(projektId: string, karteId: string, ich: Benutzer, darfSchreiben: boolean) {
  const [z, setZ] = useState<Zustand | null>(null);
  const [fehler, setFehler] = useState('');
  const [anwesend, setAnwesend] = useState<MindmapAnwesend[]>([]);
  const [konflikt, setKonflikt] = useState<Konflikt | null>(null);
  const [geloescht, setGeloescht] = useState(false);
  const [notizTakt, setNotizTakt] = useState<Record<string, number>>({});
  const [anlagenTakt, setAnlagenTakt] = useState<Record<string, number>>({});
  const pfad = modulPfad(projektId, 'mindmap', `karten/${karteId}`);
  const kanalName = kanal(projektId, 'mindmap', karteId);
  const zRef = useRef(z);
  zRef.current = z;

  const neuLaden = useCallback(async () => {
    try {
      setZ(alsZustand(await api.get<MindmapStand>(pfad)));
      setFehler('');
    } catch (e) {
      if (e instanceof ApiFehler && e.status === 404) setGeloescht(true);
      setFehler(e instanceof Error ? e.message : 'Fehler');
    }
  }, [pfad]);

  useEffect(() => {
    setZ(null);
    setGeloescht(false);
    void neuLaden();
  }, [neuLaden]);

  /** Knoten übernehmen – ältere Stände werden verworfen (außer `immer`). */
  const uebernehmen = useCallback((liste: MindmapKnoten[], immer = false) => {
    setZ((alt) => {
      if (!alt) return alt;
      const knoten = { ...alt.knoten };
      for (const k of liste) {
        const da = knoten[k.id];
        if (immer || !da || k.version >= da.version) knoten[k.id] = k;
      }
      return { ...alt, knoten };
    });
  }, []);

  const entfernen = useCallback((ids: string[]) => {
    setZ((alt) => {
      if (!alt) return alt;
      const knoten = { ...alt.knoten };
      for (const id of ids) delete knoten[id];
      return { ...alt, knoten };
    });
  }, []);

  useLive(kanalName, (roh) => {
    const d = roh as MindmapLiveVomServer;
    switch (d.typ) {
      case 'stand':
        setZ(alsZustand(d));
        setGeloescht(false);
        break;
      case 'knoten':
        uebernehmen(d.knoten);
        break;
      case 'entfernt':
        entfernen(d.ids);
        break;
      case 'spalten':
        setZ((alt) => (alt ? { ...alt, spalten: d.spalten } : alt));
        break;
      case 'karte':
        setZ((alt) => (alt ? { ...alt, karte: d.karte } : alt));
        break;
      case 'karte-geloescht':
        setGeloescht(true);
        break;
      case 'notizen':
        setNotizTakt((t) => ({ ...t, [d.knotenId]: (t[d.knotenId] ?? 0) + 1 }));
        break;
      case 'anwesenheit':
        setAnwesend(d.personen);
        break;
    }
  });

  const baum = useMemo(() => baueBaum(Object.values(z?.knoten ?? {})), [z?.knoten]);
  const baumRef = useRef(baum);
  baumRef.current = baum;

  const fehlerZeigen = (e: unknown) => setFehler(e instanceof Error ? e.message : 'Fehler');

  // Änderungen nacheinander senden – sonst könnte ein Unterpunkt vor seinem Elternpunkt ankommen.
  const kette = useRef<Promise<unknown>>(Promise.resolve());
  const nacheinander = useCallback(<T,>(fn: () => Promise<T>): Promise<T> => {
    const p = kette.current.then(fn, fn);
    kette.current = p.catch(() => undefined);
    return p;
  }, []);

  // ---------------------------------------------------------------- Rückgängig / Wiederholen
  // Jeder merkt sich nur seine eigenen Schritte. Rückgängig ist selbst eine normale Änderung mit Ausgangswert –
  // hat jemand anderes inzwischen dasselbe Feld geändert, kommt der Konfliktdialog statt stillem Überschreiben.
  const rueckStapel = useRef<Schritt[]>([]);
  const vorStapel = useRef<Schritt[]>([]);
  const [stapelTakt, setStapelTakt] = useState(0);
  const merken = useCallback((s: Schritt) => {
    rueckStapel.current = [...rueckStapel.current.slice(-99), s];
    vorStapel.current = [];
    setStapelTakt((t) => t + 1);
  }, []);
  const aktionen = useRef<Aktionen | null>(null);

  const anlegen = useCallback(
    async (elternId: string, index: number, titel: string, extra: KnotenFelder = {}, id = neueId(), merk = true) => {
      const vorlaeufig: MindmapKnoten = {
        id,
        karteId,
        elternId,
        reihenfolge: zwischenwert(baumRef.current, elternId, index),
        titel,
        beschreibung: extra.beschreibung ?? '',
        farbe: extra.farbe ?? null,
        symbol: extra.symbol ?? null,
        werte: {},
        anlagen: 0,
        notizen: 0,
        version: 0,
        geaendertVon: ich.id,
        geaendertVonName: ich.anzeigename,
        geaendertAm: new Date().toISOString(),
      };
      uebernehmen([vorlaeufig], true);
      try {
        uebernehmen([await nacheinander(() => api.post<MindmapKnoten>(`${pfad}/knoten`, { id, elternId, index, titel, ...extra }))]);
        setFehler('');
        if (merk) {
          merken({
            text: `„${titel}“ anlegen`,
            rueck: () => aktionen.current!.loeschen(id, false),
            vor: () => aktionen.current!.wiederherstellen(id),
          });
        }
        return id;
      } catch (e) {
        entfernen([id]);
        fehlerZeigen(e);
        return id;
      }
    },
    [karteId, pfad, ich, uebernehmen, entfernen, nacheinander, merken],
  );

  const aendern = useCallback(
    async (id: string, felder: KnotenFelder, basis?: KnotenFelder, erzwingen = false, merk = true): Promise<boolean> => {
      const alt = zRef.current?.knoten[id];
      if (!alt) return false;
      const neu: MindmapKnoten = { ...alt, ...felder, werte: { ...alt.werte } } as MindmapKnoten;
      for (const [s, w] of Object.entries(felder.werte ?? {})) {
        if (w === null || w === '') delete neu.werte[s];
        else neu.werte[s] = w;
      }
      uebernehmen([neu], true);
      try {
        const r = await nacheinander(() => patchKnoten(`${pfad}/knoten/${id}`, { ...felder, basis, erzwingen }));
        if (r.ok) {
          uebernehmen([r.knoten], true);
          setFehler('');
          if (merk) {
            // Vorher- und Nachher-Werte genau der geänderten Felder (Nachher so, wie der Server sie gespeichert hat)
            const vorher = felderVon(alt, felder);
            const nachher = felderVon(r.knoten, felder);
            if (JSON.stringify(vorher) !== JSON.stringify(nachher)) {
              merken({
                text: `„${r.knoten.titel}“ ändern`,
                rueck: () => aktionen.current!.aendern(id, vorher, nachher, false, false),
                vor: () => aktionen.current!.aendern(id, nachher, vorher, false, false),
              });
            }
          }
          return true;
        }
        uebernehmen([r.konflikt.knoten], true);
        setKonflikt({ knotenId: id, server: r.konflikt.knoten, meine: felder, felder: r.konflikt.felder });
        return false;
      } catch (e) {
        uebernehmen([alt], true);
        fehlerZeigen(e);
        return false;
      }
    },
    [pfad, uebernehmen, nacheinander, merken],
  );

  const verschieben = useCallback(
    async (id: string, elternId: string, index: number, merk = true): Promise<boolean> => {
      const alt = zRef.current?.knoten[id];
      if (!alt?.elternId) return false;
      const altEltern = alt.elternId;
      const altIndex = (baumRef.current.kinder.get(altEltern) ?? []).findIndex((x) => x.id === id);
      uebernehmen([{ ...alt, elternId, reihenfolge: zwischenwert(baumRef.current, elternId, index, id) }], true);
      try {
        uebernehmen(await nacheinander(() => api.post<MindmapKnoten[]>(`${pfad}/knoten/${id}/verschieben`, { elternId, index })));
        setFehler('');
        if (merk) {
          merken({
            text: `„${alt.titel}“ verschieben`,
            rueck: () => aktionen.current!.verschieben(id, altEltern, Math.max(0, altIndex), false),
            vor: () => aktionen.current!.verschieben(id, elternId, index, false),
          });
        }
        return true;
      } catch (e) {
        fehlerZeigen(e);
        void neuLaden();
        return false;
      }
    },
    [pfad, uebernehmen, neuLaden, nacheinander, merken],
  );

  const loeschen = useCallback(
    async (id: string, merk = true): Promise<boolean> => {
      const titel = zRef.current?.knoten[id]?.titel ?? '';
      entfernen([id, ...nachkommen(baumRef.current, id)]);
      try {
        const r = await nacheinander(() =>
          api.delete<{ ids: string[]; anlagen: number }>(`${pfad}/knoten/${id}`),
        );
        setFehler('');
        if (merk) {
          merken({
            text: `„${titel}“ löschen`,
            rueck: () => aktionen.current!.wiederherstellen(id),
            vor: () => aktionen.current!.loeschen(id, false),
          });
        }
        return !!r;
      } catch (e) {
        fehlerZeigen(e);
        void neuLaden();
        return false;
      }
    },
    [pfad, entfernen, neuLaden, nacheinander, merken],
  );

  const wiederherstellen = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        uebernehmen(await nacheinander(() => api.post<MindmapKnoten[]>(`${pfad}/knoten/${id}/zurueckholen`)));
        setFehler('');
        return true;
      } catch (e) {
        fehlerZeigen(e);
        return false;
      }
    },
    [pfad, uebernehmen, nacheinander],
  );

  aktionen.current = { anlegen, aendern, verschieben, loeschen, wiederherstellen };

  const rueckgaengig = useCallback(async () => {
    const s = rueckStapel.current.pop();
    setStapelTakt((t) => t + 1);
    if (!s) return;
    if (await s.rueck()) vorStapel.current = [...vorStapel.current, s];
    setStapelTakt((t) => t + 1);
  }, []);

  const wiederholen = useCallback(async () => {
    const s = vorStapel.current.pop();
    setStapelTakt((t) => t + 1);
    if (!s) return;
    if (await s.vor()) rueckStapel.current = [...rueckStapel.current, s];
    setStapelTakt((t) => t + 1);
  }, []);

  void stapelTakt;
  const stapel = {
    rueck: rueckStapel.current[rueckStapel.current.length - 1]?.text ?? null,
    vor: vorStapel.current[vorStapel.current.length - 1]?.text ?? null,
  };

  /** Anlagen eines Punkts neu zählen (geht auch mit Leserecht) und offene Anlagen-Listen neu laden lassen. */
  const anlagenGeaendert = useCallback(
    async (id: string) => {
      if (!zRef.current?.knoten[id]) return;
      try {
        const dateien = await api.get<unknown[]>(`/api/projekte/${projektId}/dateien?bereich=mindmap&bezug=${encodeURIComponent(id)}`);
        setZ((alt) => {
          const k = alt?.knoten[id];
          if (!alt || !k || k.anlagen === dateien.length) return alt;
          return { ...alt, knoten: { ...alt.knoten, [id]: { ...k, anlagen: dateien.length } } };
        });
        setAnlagenTakt((t) => ({ ...t, [id]: (t[id] ?? 0) + 1 }));
      } catch {
        /* z. B. Rechte entzogen – egal */
      }
    },
    [projektId],
  );

  // Der Kern meldet hochgeladene/gelöschte Anlagen auf p:<projekt>:mindmap
  useLive(kanal(projektId, 'mindmap'), (roh) => {
    const d = roh as { typ?: string; bezug?: string | null };
    if (d.typ === '__anlagen' && d.bezug) void anlagenGeaendert(d.bezug);
  });

  const spalteAnlegen = useCallback(
    async (e: { name: string; typ: SpaltenTyp; optionen?: SpaltenOption[]; waehrung?: string | null }) => {
      const s = await api.post<MindmapSpalte>(`${pfad}/spalten`, e);
      setZ((alt) => (alt && !alt.spalten.some((x) => x.id === s.id) ? { ...alt, spalten: [...alt.spalten, s] } : alt));
      return s;
    },
    [pfad],
  );

  const spalteAendern = useCallback(
    async (id: string, e: { name?: string; optionen?: SpaltenOption[]; waehrung?: string | null; index?: number }) => {
      const spalten = await api.patch<MindmapSpalte[]>(`${pfad}/spalten/${id}`, e);
      setZ((alt) => (alt ? { ...alt, spalten } : alt));
    },
    [pfad],
  );

  const spalteLoeschen = useCallback(
    async (id: string) => {
      await api.delete(`${pfad}/spalten/${id}`);
      setZ((alt) => (alt ? { ...alt, spalten: alt.spalten.filter((s) => s.id !== id) } : alt));
    },
    [pfad],
  );

  const letzterFokus = useRef('');
  const fokus = useCallback(
    (knotenId: string | null, bearbeitet: boolean) => {
      if (!darfSchreiben) return;
      const s = `${knotenId}|${bearbeitet}`;
      if (s === letzterFokus.current) return;
      letzterFokus.current = s;
      live.senden(kanalName, { typ: 'fokus', knotenId, bearbeitet });
    },
    [darfSchreiben, kanalName],
  );

  return {
    karte: z?.karte ?? null,
    spalten: z?.spalten ?? [],
    knoten: z?.knoten ?? {},
    baum,
    fehler,
    setFehler,
    anwesend,
    konflikt,
    setKonflikt,
    geloescht,
    notizTakt,
    anlagenTakt,
    pfad,
    neuLaden,
    anlegen,
    aendern,
    verschieben,
    loeschen,
    wiederherstellen,
    rueckgaengig,
    wiederholen,
    stapel,
    anlagenGeaendert,
    spalteAnlegen,
    spalteAendern,
    spalteLoeschen,
    fokus,
  };
}

export type KartenZugriff = ReturnType<typeof useKarte>;

/** Eingeklappte Äste merkt sich jeder Browser selbst (stört andere nicht). */
export function useEingeklappt(karteId: string) {
  const schluessel = `mm-zu:${karteId}`;
  const [zu, setZu] = useState<Set<string>>(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem(schluessel) ?? '[]') as string[]);
    } catch {
      return new Set();
    }
  });
  const setzen = useCallback(
    (neu: Set<string>) => {
      setZu(neu);
      try {
        localStorage.setItem(schluessel, JSON.stringify([...neu]));
      } catch {
        /* egal */
      }
    },
    [schluessel],
  );
  const umschalten = useCallback(
    (id: string, an?: boolean) => {
      const n = new Set(zu);
      if (an ?? !n.has(id)) n.add(id);
      else n.delete(id);
      setzen(n);
    },
    [zu, setzen],
  );
  return { zu, setzen, umschalten };
}
